import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axiosInstance from "../services/axios";
import { clearToken, getToken, isTokenExpired, VENDOR_SESSION_EVENT } from "../services/tokenStorage";

const AuthContext = createContext(null);

const normalizeProductCode = (value) => String(value || '').trim().toUpperCase().replace(/-/g, '_');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    
    loadMe();
  }, []);

  useEffect(() => {
    const handleSessionEvent = (event) => {
      if (event.detail?.reason === 'updated') {
        loadMe();
        return;
      }

      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener(VENDOR_SESSION_EVENT, handleSessionEvent);
    return () => window.removeEventListener(VENDOR_SESSION_EVENT, handleSessionEvent);
  }, []);

  const loadMe = async () => {
    if (!getToken() || isTokenExpired()) {
      clearToken('missing_or_expired');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const res = await axiosInstance.get("/users/me");
      if(res?.data?.status){
        setUser(res.data?.data);
        setIsAuthenticated(true);
      } else {
        clearToken();
        setIsAuthenticated(false);
      }
    } catch (error) {
      clearToken();
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };


  const logout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
    } finally {
      clearToken("logged_out");
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const productAccess = user?.productAccess || user?.vendorMembership?.productAccess || [];
  const hasProductAccess = (productCode) => {
    if (!productCode) return true;
    const normalizedCode = normalizeProductCode(productCode);
    if (['BILLING', 'ORGANIZATION', 'SUPPORT'].includes(normalizedCode)) return true;
    return productAccess.includes(normalizedCode);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        productAccess,
        hasProductAccess,
        reloadUser: loadMe,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
